import * as d3 from 'd3';
import { Block, Line, Position } from '../../types';
import { STYLES } from '../../styles/constants';
import { createBlockSelectionMenu } from '../menus/BlockSelectionMenu';
import { setupSvgLayer } from '../../utils/svgUtils';
import { createInfoModal } from './InfoModal';
import { FamlaServices } from '../../../../services';
import { isManualProcess, shouldSkipReferenceApi } from '../../../../utils/processTypeUtils';
import {
  calculateConnectionPoint,
  getVisibleConnectionSides,
  isTextAnnotationBlock,
} from '../../utils/connectionUtils';

/** Évite l’injection HTML dans les modals (contenu API / entretien). */
function escHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

interface BlockSelectionManagerCallbacks {
  onDelete: (block: Block) => void;
  onAddLine: (block: Block) => void;
  onLineCreated: (line: Line) => void;
  onLinkTask: (note: Block) => void;
  onUnlinkTask: (note: Block) => void;
  onLinkAttributes: (note: Block) => void;
  onUnlinkAttributes: (note: Block) => void;
  onAttachTextAnnotation: (block: Block) => void;
}

export class BlockSelectionManager {
  private container: d3.Selection<HTMLDivElement, unknown, null, undefined>;
  private svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  private selectedBlock: Block | null = null;
  private allBlocks: Block[] = [];
  private callbacks: BlockSelectionManagerCallbacks;
  private tempLine: d3.Selection<SVGPathElement, unknown, null, undefined> | null = null;
  private sourceBlock: Block | null = null;
  private sourceSide: string = '';
  private sourcePoint: Position | null = null;
  private connectionPoints: d3.Selection<HTMLDivElement, unknown, null, undefined>[] = [];
  private showLayers: boolean = false;
  private infoModal: d3.Selection<HTMLDivElement, unknown, null, undefined> | null = null;
  private mapId: string | "";
  private t: (key: string) => string;
  private lineLayer: string[];
  private accessToken: string;
  /** HUMAN | AI | IMAGE (backend process.type), uppercased */
  private processType: string;
  /** Après recalcul /reference/ : invalider cache pastilles (ProcessManager) */
  private onReferenceRefresh?: (block: Block) => void;

  constructor(
    container: d3.Selection<HTMLDivElement, unknown, null, undefined>,
    callbacks: BlockSelectionManagerCallbacks,
    mapId: string,
    t: (key: string) => string,
    lineLayer: string[],
    accessToken?: string,
    processType?: string,
    onReferenceRefresh?: (block: Block) => void
  ) {
    this.container = container;
    this.callbacks = callbacks;
    this.svg = setupSvgLayer(container);
    this.mapId = mapId;
    this.t = t;
    this.lineLayer = lineLayer;
    this.accessToken = accessToken || "";
    this.processType = (processType || '').toString().trim().toUpperCase();
    this.onReferenceRefresh = onReferenceRefresh;

    d3.select('body').on('click.block-selection', (event) => {
      const target = event.target as HTMLElement;
      const isBlock = target.closest('[class*="-block-container-"], .note-block');
      const isMenu = target.closest('[class^="block-selection-menu"]');
      const isConnectionPoint = target.closest('.connection-point');
      const isModal = target.closest('.info-modal');
      const isModalClose = target.closest('.modal-close-button');
      const isAttributePicker = target.closest('.note-attribute-picker');

      if (isModalClose) {
        this.closeInfoModal();
      } else if (!isBlock && !isMenu && !isConnectionPoint && !isModal && !isAttributePicker) {
        this.clearSelection();
      }
    });
  }

  public setProcessType(type: string | undefined): void {
    this.processType = (type || '').toString().trim().toUpperCase();
  }

  public setMapId(id: string): void {
    this.mapId = id ?? '';
  }

  private getFilteredLayers() {
    const allLayers = [
      { id: "1", text: this.t(`input`), type: "text", value: "" },
      { id: "2", text: this.t(`output`), type: "text", value: "" },
      { id: "3", text: this.t(`inventory`), type: "text", value: "" },
      { id: "4", text: this.t(`transfertime`), type: "minute", value: "" },
      // { id: "5", text: this.t(`link`), type: "text", value: "" }
    ];

    if (this.lineLayer === null) {
      return allLayers;
    }

    return allLayers.filter(layer => this.lineLayer!.includes(layer.id));
  }

  public updateBlocks(blocks: Block[]): void {
    this.allBlocks = [...blocks];
  }

  public getSelectedBlock(): Block | null {
    return this.selectedBlock;
  }

  public selectBlock(block: Block): void {
    // Vérifier si un menu existe déjà pour ce bloc
    const existingMenu = this.container.select(`[class^="block-selection-menu-${block.id}"]`);

    // Si c'est le même bloc ET que le menu existe déjà, ne rien faire
    if (this.selectedBlock?.id === block.id && !existingMenu.empty()) {
      return;
    }

    // Si c'est un bloc différent OU si le menu n'existe pas, créer un nouveau menu
    this.clearSelection();
    this.selectedBlock = block;

    const verificationUnavailable =
      isManualProcess(this.processType) || this.isManuallyAddedBlock(block);

    const isStickyNote = block.type === 'note' && !block.bpmnSymbol && !block.dmnSymbol && !block.vsmSymbol;

    createBlockSelectionMenu(this.container, block, {
      onDelete: this.callbacks.onDelete,
      onFetchInfo: block.type === 'note' ? undefined : (block) => this.fetchBlockInfo(block),
      infoButtonTitle: verificationUnavailable
        ? this.t('features.swimlane.verificationOnlyAiOrUpload')
        : undefined,
      onToggleLayers: block.type === 'note' ? undefined : () => this.toggleLayers(),
      onLinkTask: isStickyNote ? (note) => this.callbacks.onLinkTask(note) : undefined,
      onUnlinkTask: isStickyNote ? (note) => this.callbacks.onUnlinkTask(note) : undefined,
      onLinkAttributes: isStickyNote ? (note) => this.callbacks.onLinkAttributes(note) : undefined,
      onUnlinkAttributes: isStickyNote ? (note) => this.callbacks.onUnlinkAttributes(note) : undefined,
      onAttachTextAnnotation:
        block.type !== 'note'
          ? (target) => this.callbacks.onAttachTextAnnotation(target)
          : undefined,
    },
      this.t
    );
  }

  public clearSelection(): void {
    this.selectedBlock = null;
    this.container.selectAll('[class^="block-selection-menu"]').remove();
    this.hideConnectionPoints();
    this.cancelLinkMode();
  }

  private toggleLayers(): void {
    this.showLayers = !this.showLayers;
    this.container.selectAll('[class*="-layers-"] > div')
      .style('display', this.showLayers ? 'block' : 'none');
  }

  private isManuallyAddedBlock(block: Block): boolean {
    const source = block.meta?.source;
    return source === 'manual' || source === 'palette';
  }

  private showVerificationUnavailableHint(messageKey: string): void {
    this.closeInfoModal();
    this.infoModal = createInfoModal(
      {
        title: this.t('features.swimlane.verificationUnavailableTitle'),
        content: `<div class="p-4 text-gray-700">${escHtml(this.t(messageKey))}</div>`,
        onClose: () => this.closeInfoModal(),
      },
      this.t
    );
  }

  /** Chaque ouverture de la vérification recalcule la source (API force_refresh). */
  private async fetchBlockInfo(block: Block): Promise<void> {
    // Manual diagrams / palette-dropped symbols have no interview/document source.
    if (isManualProcess(this.processType) || this.isManuallyAddedBlock(block)) {
      this.showVerificationUnavailableHint('features.swimlane.verificationOnlyAiOrUpload');
      return;
    }

    if (shouldSkipReferenceApi(this.processType)) {
      this.showVerificationUnavailableHint('features.swimlane.verificationNotAvailableImage');
      return;
    }

    // [FIX] Vérifier que mapId n'est pas vide avant de faire l'appel API
    if (!this.mapId || this.mapId.trim() === "") {
      this.closeInfoModal();
      this.showErrorModal(this.t('features.swimlane.mapIdRequired') || 'Map ID is required for verification');
      console.warn('[BlockSelectionManager] Cannot fetch block info: mapId is empty');
      return;
    }

    // Show loading state
    this.showLoadingModal();

    try {
      // Prepare data for API call
      const requestData = {
        map_id: this.mapId,
        text: block.text,
        element_id: block.id
      };

      console.log('[BlockSelectionManager] Fetching block info:', { map_id: this.mapId, element_id: block.id, text: block.text });

      // [FIX] Passer le token d'accès pour éviter les erreurs CORS
      // Récupérer le token depuis les cookies si non fourni
      let token = this.accessToken;
      if (!token) {
        const cookies = document.cookie.split(';');
        const tokenCookie = cookies.find(c => c.trim().startsWith('access_token='));
        if (tokenCookie) {
          token = tokenCookie.split('=')[1];
        }
      }

      const response = await FamlaServices(
        "api_host",
        `/reference/?force_refresh=true`,
        "POST",
        requestData,
        token || ""
      );

      // Close loading modal
      this.closeInfoModal();

      if (response.status === 201 || response.status === 200) {
        // Accept common wrappers from backend/proxy layers.
        const responseData =
          response?.body?.data ??
          response?.body ??
          (response as any)?.data?.data ??
          (response as any)?.data;
        this.onReferenceRefresh?.(block);
        if (responseData && Array.isArray(responseData) && responseData.length > 0) {
          this.showInfoModal(block, responseData);
        } else if (responseData && Array.isArray(responseData) && responseData.length === 0) {
          this.showErrorModal(this.t('features.swimlane.noInformation'));
        } else {
          // Format de réponse inattendu
          console.error('[BlockSelectionManager] Unexpected response format:', response);
          this.showErrorModal(this.t('navigation.anErrorOcured'));
        }

      } else {
        // Show error if status is not 201
        console.error('[BlockSelectionManager] API error:', response.status, response.body);
        this.showErrorModal(this.t('navigation.anErrorOcured'));
      }
    } catch (error) {
      console.error('[BlockSelectionManager] Exception during fetchBlockInfo:', error);
      this.closeInfoModal();
      this.showErrorModal(this.t('navigation.anErrorOcured'));
    }
  }

  private showLoadingModal(): void {
    this.closeInfoModal();
    this.infoModal = createInfoModal({
      title: this.t('words.loading') + '...',
      content: `<div class="flex justify-center items-center p-4"><div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div></div><p class="text-center text-gray-600">${escHtml(this.t('words.loading'))}` + '...</p>',
      onClose: () => this.closeInfoModal()
    },
      this.t);
  }

  /**
   * Décode une entrée API HUMAN : peut contenir "Question: … / Answer: …" ou un seul extrait.
   */
  private parseHumanVerificationEntry(entry: string): {
    question?: string;
    answer?: string;
    prose?: string;
  } {
    const text = (entry || '').trim();
    if (!text) return { prose: '—' };

    const answerMatch = text.match(/(?:^|\n)\s*Answer:\s*([\s\S]+)$/i);
    if (!answerMatch) {
      return { prose: text };
    }

    const answer = answerMatch[1].trim();
    const head = text.slice(0, text.length - answerMatch[0].length).trim();
    const qMatch = head.match(/Question:\s*([\s\S]+?)\s*$/i);
    if (qMatch) {
      const question = qMatch[1].trim();
      return { question, answer };
    }
    return { answer };
  }

  private buildHumanVerificationHtml(block: Block, entries: string[]): string {
    const intro = escHtml(this.t('features.swimlane.verificationHumanIntro'));
    const stepLabel = escHtml(this.t('features.swimlane.verificationDiagramStepLabel'));
    const task = escHtml((block.text || '').trim() || '—');
    const qLabel = escHtml(this.t('words.question'));
    const aLabel = escHtml(this.t('words.answer'));
    const sourceLabel = escHtml(this.t('features.swimlane.verificationSourceText'));

    const cards = entries.map((entry, i) => {
      const parsed = this.parseHumanVerificationEntry(entry);
      const excerptTitle = escHtml(
        this.t('features.swimlane.verificationExcerpt', { n: i + 1 })
      );

      let body = '';
      if (parsed.question != null && parsed.answer != null) {
        body = `
          <div class="space-y-3 p-3">
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">${qLabel}</p>
              <div class="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">${escHtml(parsed.question)}</div>
            </div>
            <div>
              <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">${aLabel}</p>
              <div class="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">${escHtml(parsed.answer)}</div>
            </div>
          </div>`;
      } else if (parsed.answer != null) {
        body = `
          <div class="p-3">
            <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">${aLabel}</p>
            <div class="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">${escHtml(parsed.answer)}</div>
          </div>`;
      } else {
        const prose = (parsed.prose || '—').trim();
        body = `
          <div class="p-3">
            <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">${sourceLabel}</p>
            <div class="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">${escHtml(prose)}</div>
          </div>`;
      }

      return `
        <article class="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
          <header class="bg-slate-100 px-3 py-2 border-b border-gray-200">
            <h4 class="text-xs font-semibold text-slate-700 m-0">${excerptTitle}</h4>
          </header>
          ${body}
        </article>`;
    }).join('');

    return `
      <div class="bg-gray-50 p-4 rounded-lg border border-gray-200/80">
        <p class="text-sm text-gray-600 leading-relaxed mb-3 m-0">${intro}</p>
        <div class="mb-4 rounded-md bg-blue-50 border border-blue-100 px-3 py-2">
          <p class="text-xs font-semibold uppercase tracking-wide text-blue-800 m-0 mb-1">${stepLabel}</p>
          <p class="text-sm font-medium text-gray-900 m-0">${task}</p>
        </div>
        <div class="space-y-4">${cards}</div>
      </div>`;
  }

  private showInfoModal(block: Block, data: any): void {
    this.closeInfoModal();
    let formattedContent = '<div class="modal-content">';

    if (Array.isArray(data)) {
      if (typeof data[0] === 'string') {
        // HUMAN (et tout format « liste de chaînes ») : extraits lisibles + échappement HTML
        formattedContent += this.buildHumanVerificationHtml(block, data as string[]);
      } else if (typeof data[0] === 'object') {
        // Stakeholder + questions/réponses (processus AI)
        formattedContent += `
        <div class="overflow-x-auto">
          <table class="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead>
              <tr class="bg-gray-100">
                <th class="py-3 px-4 text-left font-semibold text-gray-700 border-b">${escHtml(this.t('features.swimlane.stakeholder'))}</th>
                <th class="py-3 px-4 text-left font-semibold text-gray-700 border-b">${escHtml(this.t('words.question'))}</th>
                <th class="py-3 px-4 text-left font-semibold text-gray-700 border-b">${escHtml(this.t('words.answer'))}</th>
              </tr>
            </thead>
            <tbody>
      `;
        data.forEach((stakeholderData: any) => {
          const stakeholder = escHtml(String(stakeholderData.stakeholder || '—'));
          const answers = stakeholderData.answers || [];
          answers.forEach((answer: any, index: number) => {
            const q = escHtml(String(answer.question || '—'));
            const a = escHtml(String(answer.answer || '—'));
            formattedContent += `
            <tr class="${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors">
              ${index === 0 ? `<td class="py-3 px-4 border-b border-gray-200 align-top font-medium" rowspan="${answers.length}">${stakeholder}</td>` : ''}
              <td class="py-3 px-4 border-b border-gray-200 whitespace-pre-wrap">${q}</td>
              <td class="py-3 px-4 border-b border-gray-200 whitespace-pre-wrap">${a}</td>
            </tr>
          `;
          });
        });
        formattedContent += `
            </tbody>
          </table>
        </div>
      `;
      }
    } else if (typeof data === 'object' && data !== null) {
      formattedContent += `<pre class="bg-gray-50 p-3 rounded text-sm overflow-auto max-h-60">${escHtml(JSON.stringify(data, null, 2))}</pre>`;
    } else {
      formattedContent += `<p class="p-3 bg-gray-50 rounded">${escHtml(String(data))}</p>`;
    }

    formattedContent += '</div>';

    const modalTitle =
      this.processType === 'HUMAN'
        ? this.t('features.swimlane.verificationSourceTitle')
        : this.t('features.swimlane.verificationSourceTitle');

    this.infoModal = createInfoModal({
      title: modalTitle,
      content: formattedContent,
      onClose: () => this.closeInfoModal(),
    }, this.t);
  }


  private showErrorModal(message: string): void {
    this.closeInfoModal();
    this.infoModal = createInfoModal({
      title: this.t('words.error'),
      content: `
        <div class="p-4 bg-red-50 rounded border border-red-200">
          <div class="flex items-center mb-2">
            <svg class="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
             <span class="font-medium text-red-700">${escHtml(this.t('words.error'))}</span>
          </div>
          <p class="text-red-600 whitespace-pre-wrap">${escHtml(message)}</p>
        </div>
      `,
      onClose: () => this.closeInfoModal()
    },
      this.t);
  }

  private closeInfoModal(): void {
    if (this.infoModal) {
      this.infoModal.remove();
      this.infoModal = null;
    }
    d3.select('body').on('keydown.modal', null);
  }

  public startLinkMode(block: Block): void {
    this.sourceBlock = block;
    this.showConnectionPoints(block);
  }

  private cancelLinkMode(): void {
    if (this.tempLine) {
      this.tempLine.remove();
      this.tempLine = null;
    }
    this.container.on('mousemove', null);
    this.sourceBlock = null;
    this.sourceSide = '';
    this.sourcePoint = null;
    this.hideConnectionPoints();
  }

  private hideConnectionPoints(): void {
    this.connectionPoints.forEach(point => point.remove());
    this.connectionPoints = [];
  }

  private showConnectionPoints(block: Block, isTarget: boolean = false): void {
    // Toujours dériver des dimensions/positions actuelles (drag, propriétés, fit content).
    const geom = { container: this.container };
    const sides = getVisibleConnectionSides(block);
    const points = sides.map((side) => calculateConnectionPoint(block, side, geom));

    const newPoints = points.map(point => {
      const connectionPoint = this.container
        .append('div')
        .attr('class', `connection-point ${point.side}`)
        .attr('data-block-id', block.id)
        .style('position', 'absolute')
        .style('width', '10px')
        .style('height', '10px')
        .style('background-color', isTarget ? '#10b981' : STYLES.colors.connectionPoint)
        .style('border', '2px solid transparent')
        .style('border-radius', '50%')
        .style('transform', 'translate(-50%, -50%)')
        .style('cursor', 'pointer')
        .style('z-index', '100')
        .style('left', `${point.x}px`)
        .style('top', `${point.y}px`)
        .style('transition', 'all 0.2s ease')
        .style('box-shadow', '0 0 0 0 rgba(66, 153, 225, 0.5)');

      connectionPoint
        .on('mouseenter', function () {
          d3.select(this)
            .style('transform', 'translate(-50%, -50%) scale(1.5)')
            .style('background-color', isTarget ? '#059669' : '#2563eb')
            .style('border-color', '#ffffff')
            .style('box-shadow', '0 0 0 4px rgba(66, 153, 225, 0.3)');
        })
        .on('mouseleave', function () {
          d3.select(this)
            .style('transform', 'translate(-50%, -50%) scale(1)')
            .style('background-color', isTarget ? '#10b981' : STYLES.colors.connectionPoint)
            .style('border-color', 'transparent')
            .style('box-shadow', '0 0 0 0 rgba(66, 153, 225, 0.5)');
        });

      if (isTarget) {
        connectionPoint.on('click', (event) => {
          event.stopPropagation();
          if (this.sourceBlock && this.sourceSide) {
            const isAssociation =
              isTextAnnotationBlock(this.sourceBlock) || isTextAnnotationBlock(block);
            const fromSide = isTextAnnotationBlock(this.sourceBlock)
              ? 'left'
              : (this.sourceSide as any);
            const toSide = isTextAnnotationBlock(block) ? 'left' : (point.side as any);
            const newLine: Line = {
              id: `${isAssociation ? 'association' : 'line'}-${Date.now()}`,
              type: isAssociation ? 'association' : 'line',
              sourceId: "",
              targetId: "",
              from: this.sourceBlock.id,
              to: block.id,
              fromSide,
              toSide,
              text: "",
              ...(!isAssociation && this.sourceBlock.type === 'decision'
                ? { labelPositionFromStart: 0.22, exitLabel: '' }
                : {}),
              layers: isAssociation ? undefined : (this.getFilteredLayers() as any),
            };

            this.callbacks.onLineCreated(newLine);
            this.cancelLinkMode();
          }
        });
      } else {
        connectionPoint.on('click', (event) => {
          event.stopPropagation();

          d3.select(event.currentTarget)
            .style('transform', 'translate(-50%, -50%) scale(1.2)')
            .style('background-color', '#1d4ed8')
            .style('box-shadow', '0 0 0 6px rgba(66, 153, 225, 0.4)');

          this.sourceSide = point.side;
          this.sourcePoint = { x: point.x, y: point.y };
          this.hideConnectionPoints();

          this.allBlocks
            .filter(b => b.id !== block.id)
            .forEach(b => this.showConnectionPoints(b, true));

          if (this.tempLine) {
            this.tempLine.remove();
          }

          const previewIsAssociation = isTextAnnotationBlock(block);
          this.tempLine = this.svg
            .append('path')
            .attr('stroke', STYLES.colors.arrow)
            .attr('stroke-width', '2')
            .attr('fill', 'none')
            .attr('stroke-dasharray', previewIsAssociation ? '4 4' : null)
            .attr('marker-end', previewIsAssociation ? null : 'url(#arrowhead)');

          this.container.on('mousemove', (event) => {
            if (this.tempLine && this.sourcePoint) {
              const containerRect = this.container.node()?.getBoundingClientRect();
              if (!containerRect) return;

              const mouseX = event.clientX - containerRect.left;
              const mouseY = event.clientY - containerRect.top;

              const path = d3.path();
              path.moveTo(this.sourcePoint.x, this.sourcePoint.y);
              path.lineTo(mouseX, mouseY);
              this.tempLine.attr('d', path.toString());
            }
          });
        });
      }

      return connectionPoint;
    });

    if (!isTarget) {
      this.connectionPoints = newPoints;
    } else {
      this.connectionPoints = [...this.connectionPoints, ...newPoints];
    }
  }

  public syncConnectionPointsForBlock(block: Block): void {
    const geom = { container: this.container };
    getVisibleConnectionSides(block).forEach((side) => {
      const point = calculateConnectionPoint(block, side, geom);
      this.container
        .selectAll(`.connection-point.${side}[data-block-id="${block.id}"]`)
        .style('left', `${point.x}px`)
        .style('top', `${point.y}px`);
    });

    if (
      this.sourceBlock?.id === block.id &&
      this.sourceSide &&
      this.tempLine &&
      this.sourcePoint
    ) {
      const refreshed = calculateConnectionPoint(
        block,
        this.sourceSide as 'top' | 'right' | 'bottom' | 'left',
        geom
      );
      this.sourcePoint = { x: refreshed.x, y: refreshed.y };
    }
  }
}